/**
 * Directory (OU / Groups) – Folders → Collections → Devices.
 * Create/manage folders; collections under folders contain devices.
 * Persisted in Postgres via tenant directory API (localStorage is a cache/mirror only).
 */
import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, Laptop, RefreshCw, Plus, Pencil, Trash2, Search, Filter, FolderInput, MoreVertical, ArrowRightLeft } from 'lucide-react';
import { apiService, Device } from '../services/api';
import { isLastSeenOnline, loadOnlineGrace } from '../utils/agentOnline';
import { useToast } from '../contexts/ToastContext';
import { ConfirmDialog, PromptDialog } from './ConfirmDialog';
import {
  STORAGE_FOLDERS,
  STORAGE_COLLECTIONS,
} from '../constants/storageKeys';
import {
  DIRECTORY_UPDATED_EVENT,
  DIRECTORY_ROOT_ID,
  UNASSIGNED_COLLECTION_ID,
  loadDirectory,
  saveDirectory,
  seedDefaultDirectory,
} from '../services/directoryStore';

export {
  STORAGE_FOLDERS,
  STORAGE_COLLECTIONS,
  DIRECTORY_UPDATED_EVENT,
  DIRECTORY_ROOT_ID,
  UNASSIGNED_COLLECTION_ID,
  seedDefaultDirectory,
};

interface CustomFolder {
  id: string;
  name: string;
  isDefault?: boolean;
}

interface CustomCollection {
  id: string;
  name: string;
  folderId: string;
  deviceIds?: string[];
  criticalDeviceIds?: string[];
  createdAt?: string;
}

function getDeviceHealth(device: Device): 'active' | 'inactive' | 'critical' {
  if (!device.last_seen) return 'critical';
  const now = new Date().getTime();
  const lastSeen = new Date(device.last_seen).getTime();
  const diffDays = (now - lastSeen) / (1000 * 60 * 60 * 24);
  if (diffDays < 7) return 'active';
  if (diffDays < 15) return 'inactive';
  return 'critical';
}

function isDeviceOnline(device: Device): boolean {
  return isLastSeenOnline(device.last_seen);
}

function formatRelativeTime(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const now = new Date().getTime();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  if (diffMs < 0) return '';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Last seen just now';
  if (mins < 60) return `Last seen ${mins} min${mins > 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Last seen ${hrs} hr${hrs > 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `Last seen ${days} day${days > 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  return `Last seen ${months} month${months > 1 ? 's' : ''} ago`;
}

function getDeviceMeta(device: Device & { device_metadata?: any; ip?: string }) {
  const metadata = device.device_metadata || {};
  const hostname = metadata.hostname || metadata.Hostname || device.name || '';
  let rawSerial = metadata.serial_number || metadata.serialNumber || metadata.system_serial || null;
  let rawUser = metadata.current_user || metadata.last_logged_on_user || null;
  const hostnamePatterns = ['DESKTOP-', 'LAPTOP-', 'PC-', 'WIN-', 'COMPUTER-'];
  const isSerialHostname = rawSerial && hostnamePatterns.some((p) => (rawSerial || '').toUpperCase().startsWith(p));
  const isUserSerial = rawUser && /^[A-Z0-9]{6,20}$/.test(rawUser) && !rawUser.includes('.') && !rawUser.includes('-') && rawUser !== hostname;
  let serial = rawSerial;
  let lastUser = rawUser || metadata.logged_in_user || metadata.user || metadata.username || null;
  if (isSerialHostname && isUserSerial) {
    serial = rawUser;
    lastUser = null;
  } else {
    if (serial && (serial === hostname || hostnamePatterns.some((p) => (serial || '').toUpperCase().startsWith(p)))) serial = null;
    if (lastUser && /^[A-Z0-9]{6,20}$/.test(lastUser) && !lastUser.includes('.') && lastUser !== serial) lastUser = null;
  }
  return {
    serialNumber: serial || 'N/A',
    lastLoggedOnUser: lastUser || metadata.last_logged_on_user || metadata.logged_in_user || metadata.user || 'Unknown',
    ipAddress: device.ip_address || device.ip || metadata.ip_address || '—',
  };
}

interface TreeNode {
  id: string;
  name: string;
  type: 'folder' | 'collection';
  folderId?: string;
  deviceIds?: string[];
}

const DEFAULT_FOLDER: CustomFolder = { id: 'folder-default', name: 'General', isDefault: true };
const DEPARTMENTS_FOLDER: CustomFolder = { id: 'folder-departments', name: 'Departments', isDefault: true };

function dedupeFolders(folders: CustomFolder[]): CustomFolder[] {
  const seen = new Set<string>();
  return folders.filter((f) => {
    if (seen.has(f.id)) return false;
    seen.add(f.id);
    return true;
  });
}

function dedupeCollections(collections: CustomCollection[]): CustomCollection[] {
  const seen = new Set<string>();
  return collections.filter((c) => {
    if (seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  });
}

const AddDevicesModal: React.FC<{
  devices: Device[];
  existingIds: Set<string>;
  onClose: () => void;
  onConfirm: (deviceIds: string[]) => void;
}> = ({ devices, existingIds, onClose, onConfirm }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const available = devices.filter((d) => !existingIds.has(d.device_id || (d as any).id));
  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(available.map((d) => d.device_id || (d as any).id)));
  const clearAll = () => setSelected(new Set());
  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h4 className="font-semibold text-slate-800">Add devices to collection</h4>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <div className="p-4 max-h-64 overflow-y-auto">
          {available.length === 0 ? (
            <p className="text-slate-500 text-sm">All devices are already in this collection.</p>
          ) : (
            <>
              <div className="flex gap-2 mb-3">
                <button type="button" onClick={selectAll} className="text-xs text-indigo-600 hover:underline">Select all</button>
                <button type="button" onClick={clearAll} className="text-xs text-slate-500 hover:underline">Clear</button>
              </div>
              <div className="space-y-1">
                {available.map((d) => {
                  const id = d.device_id || (d as any).id;
                  return (
                    <label key={id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" checked={selected.has(id)} onChange={() => toggle(id)} />
                      <span className="text-sm font-medium">{d.name || id}</span>
                      <span className="text-xs text-slate-500">{d.os || d.platform}</span>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button type="button" onClick={() => onConfirm(Array.from(selected))} disabled={selected.size === 0} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
            Add {selected.size} device{selected.size !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </>
  );
};

const MoveToCollectionModal: React.FC<{
  deviceNames: string[];
  collections: CustomCollection[];
  folders: CustomFolder[];
  currentCollectionId?: string;
  onClose: () => void;
  onConfirm: (collectionId: string) => void;
}> = ({ deviceNames, collections, folders, currentCollectionId, onClose, onConfirm }) => {
  const [selected, setSelected] = useState('');
  const available = collections.filter((c) => c.id !== currentCollectionId && c.id !== UNASSIGNED_COLLECTION_ID);
  const folderMap = new Map(folders.map((f) => [f.id, f.name]));
  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h4 className="font-semibold text-slate-800">Move to Collection</h4>
          <p className="text-xs text-slate-500 mt-1">
            {deviceNames.length === 1
              ? `Move "${deviceNames[0]}" to a collection`
              : `Move ${deviceNames.length} devices to a collection`}
          </p>
        </div>
        <div className="p-4 max-h-64 overflow-y-auto">
          {available.length === 0 ? (
            <p className="text-slate-500 text-sm">No collections available. Create a collection first.</p>
          ) : (
            <div className="space-y-1">
              {available.map((c) => (
                <label
                  key={c.id}
                  className={`flex items-center gap-3 py-2.5 px-3 rounded-lg cursor-pointer transition-colors ${selected === c.id ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'}`}
                >
                  <input
                    type="radio"
                    name="target-collection"
                    checked={selected === c.id}
                    onChange={() => setSelected(c.id)}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-800">{c.name}</span>
                    <span className="text-xs text-slate-400 ml-2">{folderMap.get(c.folderId) || ''}</span>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(selected)}
            disabled={!selected}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            Move
          </button>
        </div>
      </div>
    </>
  );
};

const CollectionDeviceView: React.FC<{
  collectionName: string;
  displayedDevices: Device[];
  loading: boolean;
  isUnassigned: boolean;
  onAddDevices: () => void;
  onRemoveDevice: (id: string) => void;
  onMoveCollection?: () => void;
  onDeleteCollection?: () => void;
  onMoveDevices: (deviceIds: string[]) => void;
  onDeleteDevices: (deviceIds: string[]) => void;
  collectionSearch: string;
  onSearchChange: (v: string) => void;
  checkedDevices: Record<string, boolean>;
  onCheckedChange: (v: Record<string, boolean>) => void;
  getDeviceMeta: (d: Device & { device_metadata?: any; ip?: string }) => { serialNumber: string; lastLoggedOnUser: string; ipAddress: string };
}> = ({ collectionName, displayedDevices, loading, isUnassigned, onAddDevices, onRemoveDevice, onMoveCollection, onDeleteCollection, onMoveDevices, onDeleteDevices, collectionSearch, onSearchChange, checkedDevices, onCheckedChange, getDeviceMeta }) => {
  const deviceId = (d: Device) => d.device_id || (d as any).id;
  const toggleAll = () => {
    if (displayedDevices.length === 0) return;
    const allChecked = displayedDevices.every((d) => checkedDevices[deviceId(d)]);
    if (allChecked) onCheckedChange({});
    else onCheckedChange(Object.fromEntries(displayedDevices.map((d) => [deviceId(d), true])));
  };
  const toggleOne = (id: string) => onCheckedChange({ ...checkedDevices, [id]: !checkedDevices[id] });
  const count = displayedDevices.length;
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; openUp: boolean } | null>(null);
  const menuBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const selectedIds = Object.entries(checkedDevices).filter(([, v]) => v).map(([k]) => k);
  const selectedCount = selectedIds.length;

  useLayoutEffect(() => {
    if (!openMenu) {
      setMenuPos(null);
      return;
    }
    const btn = menuBtnRefs.current[openMenu];
    if (!btn) return;
    const update = () => {
      const r = btn.getBoundingClientRect();
      const menuHeight = 160;
      const menuWidth = 176; // w-44
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < menuHeight + 8 && r.top > menuHeight;
      const top = openUp ? r.top - 4 : r.bottom + 4;
      const left = Math.max(8, Math.min(r.right - menuWidth, window.innerWidth - menuWidth - 8));
      setMenuPos({ top, left, openUp });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [openMenu]);

  return (
    <div className="space-y-4">
      {/* Compact header: breadcrumb + title + actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
            <span>Directory</span>
            <ChevronRight size={14} className="text-slate-400" />
            <span className="text-slate-700 font-medium">{collectionName}</span>
          </div>
          <h2 className="text-xl font-semibold text-slate-900">{collectionName}</h2>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!isUnassigned && (
            <>
              <button
                type="button"
                onClick={onAddDevices}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
              >
                <Plus size={16} /> Add Devices
              </button>
              {onMoveCollection && (
                <button
                  type="button"
                  onClick={onMoveCollection}
                  className="flex items-center gap-2 px-3 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium"
                  title="Move to another folder"
                >
                  <FolderInput size={16} /> Move
                </button>
              )}
              {onDeleteCollection && (
                <button
                  type="button"
                  onClick={onDeleteCollection}
                  className="flex items-center gap-2 px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 text-sm font-medium"
                  title="Delete collection"
                >
                  <Trash2 size={16} /> Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Search and filters - inline with table */}
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search endpoints..."
            value={collectionSearch}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
          />
        </div>
        <button type="button" className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
          <Filter size={16} /> Filters
        </button>
      </div>

      {/* Bulk actions toolbar */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-indigo-50 border border-indigo-200 rounded-lg">
          <span className="text-sm font-medium text-indigo-700">{selectedCount} selected</span>
          <div className="h-4 w-px bg-indigo-200" />
          <button
            type="button"
            onClick={() => onMoveDevices(selectedIds)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-white border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
          >
            <ArrowRightLeft size={14} /> Move to Collection
          </button>
          <button
            type="button"
            onClick={() => onDeleteDevices(selectedIds)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            <Trash2 size={14} /> Delete
          </button>
          <button
            type="button"
            onClick={() => onCheckedChange({})}
            className="ml-auto text-xs text-slate-500 hover:text-slate-700"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Device table — overflow only on scroll wrapper; menus use a portal so they are not clipped */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        {loading ? (
          <div className="flex items-center gap-2 py-16 text-slate-500 justify-center">
            <span className="inline-block w-6 h-6 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
            Loading...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider text-xs">
                <tr>
                  <th className="px-4 py-3 w-10 text-center">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      checked={displayedDevices.length > 0 && displayedDevices.every((d) => checkedDevices[deviceId(d)])}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="px-4 py-3">Health</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Device Name</th>
                  <th className="px-4 py-3">User/Owner</th>
                  <th className="px-4 py-3">Last Logged On User</th>
                  <th className="px-4 py-3">Serial Number</th>
                  <th className="px-4 py-3">OS Platform</th>
                  <th className="px-4 py-3">IP Address</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayedDevices.map((device) => {
                  const id = deviceId(device);
                  const meta = getDeviceMeta(device as any);
                  const online = isDeviceOnline(device);
                  const health = getDeviceHealth(device);
                  const healthColors = {
                    active: 'bg-green-100 text-green-700',
                    inactive: 'bg-amber-100 text-amber-700',
                    critical: 'bg-red-100 text-red-700',
                  };
                  const healthDots = {
                    active: 'bg-green-500',
                    inactive: 'bg-amber-500',
                    critical: 'bg-red-500',
                  };
                  return (
                    <tr key={id} className={`hover:bg-slate-50 ${checkedDevices[id] ? 'bg-blue-50/50' : ''}`}>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          checked={!!checkedDevices[id]}
                          onChange={() => toggleOne(id)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${healthColors[health]}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${healthDots[health]}`} />
                          {health.charAt(0).toUpperCase() + health.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${online ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                            {online ? 'Online' : 'Offline'}
                          </span>
                          {!online && device.last_seen && (
                            <div className="text-[10px] text-slate-400 mt-0.5">{formatRelativeTime(device.last_seen)}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-700">{device.name || id}</td>
                      <td className="px-4 py-3 text-slate-600">{(() => {
                        const raw = (device as any).department || (device as any).dept || '';
                        if (!raw || raw === 'None' || raw.startsWith('dept-custom-')) return `${collectionName} User`;
                        return raw;
                      })()}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{meta.lastLoggedOnUser}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{meta.serialNumber}</td>
                      <td className="px-4 py-3 text-slate-600">{device.os || device.platform || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{meta.ipAddress}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          ref={(el) => { menuBtnRefs.current[id] = el; }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenu(openMenu === id ? null : id);
                          }}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                          aria-haspopup="menu"
                          aria-expanded={openMenu === id}
                        >
                          <MoreVertical size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {displayedDevices.length === 0 && (
              <div className="py-12 text-center text-slate-500 text-sm">
                {isUnassigned ? 'All devices are assigned to collections.' : 'No devices in this collection.'}
              </div>
            )}
          </div>
        )}
      </div>

      {openMenu && menuPos && createPortal(
        <>
          <div className="fixed inset-0 z-[80]" onClick={() => setOpenMenu(null)} aria-hidden />
          <div
            role="menu"
            className="fixed z-[90] w-44 bg-white rounded-lg shadow-lg border border-slate-200 py-1"
            style={{
              top: menuPos.openUp ? undefined : menuPos.top,
              bottom: menuPos.openUp ? window.innerHeight - menuPos.top : undefined,
              left: menuPos.left,
            }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpenMenu(null); onMoveDevices([openMenu]); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <ArrowRightLeft size={14} className="text-slate-400" /> Move to Collection
            </button>
            {!isUnassigned && (
              <button
                type="button"
                role="menuitem"
                onClick={() => { setOpenMenu(null); onRemoveDevice(openMenu); }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <FolderInput size={14} className="text-slate-400" /> Remove from Collection
              </button>
            )}
            <div className="border-t border-slate-100 my-1" />
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpenMenu(null); onDeleteDevices([openMenu]); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 size={14} /> Delete Device
            </button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

function tenantScopedKey(base: string): string {
  try {
    const raw = localStorage.getItem('uem_user');
    const u = raw ? JSON.parse(raw) : null;
    const scope = String(u?.tenant_id || u?.tenant_code || '');
    if (scope) {
      const scoped = localStorage.getItem(`${base}:${scope}`);
      if (scoped) return `${base}:${scope}`;
    }
  } catch {
    /* ignore */
  }
  return base;
}

function mirrorFolders(): CustomFolder[] {
  try {
    const s = localStorage.getItem(tenantScopedKey(STORAGE_FOLDERS));
    const parsed = s ? JSON.parse(s) : [];
    let list = Array.isArray(parsed) && parsed.length > 0 ? parsed : [DEFAULT_FOLDER, DEPARTMENTS_FOLDER];
    list = dedupeFolders(list);
    try {
      const raw = localStorage.getItem('uem_user');
      const u = raw ? JSON.parse(raw) : null;
      const code = String(u?.tenant_code || '').toUpperCase();
      if (code && code !== 'NUTRINIUM') {
        list = list.filter(
          (f) => f.id !== 'folder-nutrinium' && String(f.name).toLowerCase() !== 'nutrinium'
        );
      }
    } catch {
      /* ignore */
    }
    return list;
  } catch {
    return [DEFAULT_FOLDER, DEPARTMENTS_FOLDER];
  }
}

function mirrorCollections(): CustomCollection[] {
  try {
    const s = localStorage.getItem(tenantScopedKey(STORAGE_COLLECTIONS));
    const parsed = s ? JSON.parse(s) : [];
    const list = dedupeCollections(Array.isArray(parsed) ? parsed : []);
    // Hide Nutrinium brand collections if they linger in a non-scoped cache
    try {
      const raw = localStorage.getItem('uem_user');
      const u = raw ? JSON.parse(raw) : null;
      const code = String(u?.tenant_code || '').toUpperCase();
      if (code && code !== 'NUTRINIUM') {
        return list.filter(
          (c) =>
            c.id !== 'coll-aws-servers' &&
            c.id !== 'coll-bi' &&
            c.folderId !== 'folder-nutrinium' &&
            !['AWS servers', 'BI'].includes(c.name)
        );
      }
    } catch {
      /* ignore */
    }
    return list;
  } catch {
    return [];
  }
}

interface DirectoryViewProps {
  initialSelectedId?: string | null;
}

type ConfirmState =
  | { kind: 'delete-folder' | 'delete-collection'; id: string; name: string }
  | { kind: 'delete-devices'; ids: string[]; names: string[] }
  | null;

type PromptState =
  | { kind: 'create-folder' }
  | { kind: 'create-collection'; folderId: string }
  | { kind: 'rename'; id: string; type: 'folder' | 'collection'; currentName: string }
  | { kind: 'move-collection'; collectionId: string; collectionName: string; options: CustomFolder[] }
  | null;

const DirectoryView: React.FC<DirectoryViewProps> = ({ initialSelectedId }) => {
  const toast = useToast();
  const [devices, setDevices] = useState<Device[]>([]);
  const [folders, setFolders] = useState<CustomFolder[]>(mirrorFolders);
  const [collections, setCollections] = useState<CustomCollection[]>(mirrorCollections);
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
  const [confirmState, setConfirmState] = useState<ConfirmState>(null);
  const [promptState, setPromptState] = useState<PromptState>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    if (!initialSelectedId) {
      setSelectedNode(null);
      return;
    }
    if (initialSelectedId === DIRECTORY_ROOT_ID) {
      setSelectedNode(null);
      return;
    }
    if (initialSelectedId === UNASSIGNED_COLLECTION_ID) {
      setSelectedNode({ id: UNASSIGNED_COLLECTION_ID, name: 'Unassigned', type: 'collection' });
      return;
    }
    const folder = folders.find((f) => f.id === initialSelectedId);
    let coll = collections.find((c) => c.id === initialSelectedId);
    if (!coll) {
      coll = collections.find((c) => c.name?.toLowerCase() === initialSelectedId.toLowerCase());
    }
    if (folder) {
      setSelectedNode({ id: folder.id, name: folder.name, type: 'folder', folderId: folder.id });
    } else if (coll) {
      setSelectedNode({ id: coll.id, name: coll.name, type: 'collection', folderId: coll.folderId, deviceIds: coll.deviceIds });
    }
  }, [initialSelectedId, folders, collections]);
  const [loading, setLoading] = useState(true);
  const [addDevicesOpen, setAddDevicesOpen] = useState(false);
  const [moveDevicesOpen, setMoveDevicesOpen] = useState(false);
  const [deviceIdsToMove, setDeviceIdsToMove] = useState<string[]>([]);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [checkedDevices, setCheckedDevices] = useState<Record<string, boolean>>({});
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let c = false;
    setLoading(true);
    Promise.all([
      apiService.getDevices(),
      loadDirectory(),
      loadOnlineGrace(),
    ])
      .then(([d, dir]) => {
        if (c) return;
        setDevices(Array.isArray(d) ? d : []);
        setFolders(dedupeFolders(dir.folders as CustomFolder[]));
        setCollections(dedupeCollections(dir.collections as CustomCollection[]));
      })
      .catch(() => {
        if (!c) {
          setDevices([]);
          setFolders(mirrorFolders());
          setCollections(mirrorCollections());
        }
      })
      .finally(() => { if (!c) setLoading(false); });
    return () => { c = true; };
  }, []);

  const persistDirectory = async (f: CustomFolder[], cols: CustomCollection[]) => {
    setFolders(f);
    setCollections(cols);
    try {
      const saved = await saveDirectory(f, cols);
      setFolders(dedupeFolders(saved.folders as CustomFolder[]));
      setCollections(dedupeCollections(saved.collections as CustomCollection[]));
    } catch (e) {
      console.warn('[DirectoryView] persist failed:', e);
      window.dispatchEvent(new CustomEvent(DIRECTORY_UPDATED_EVENT));
    }
  };

  const saveFolders = (f: CustomFolder[]) => {
    void persistDirectory(f, collections);
  };

  const saveCollections = (cols: CustomCollection[]) => {
    void persistDirectory(folders, cols);
  };

  const collectionsByFolder = useMemo(() => {
    const map = new Map<string, CustomCollection[]>();
    collections.forEach((c) => {
      const fid = c.folderId || 'folder-default';
      if (!map.has(fid)) map.set(fid, []);
      map.get(fid)!.push(c);
    });
    map.forEach((arr) => arr.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    return map;
  }, [collections]);

  const displayedDevices = useMemo(() => {
    if (!selectedNode || selectedNode.type !== 'collection') return [];
    if (selectedNode.id === UNASSIGNED_COLLECTION_ID) {
      const assignedIds = new Set<string>();
      collections.forEach((c) => (c.deviceIds || []).forEach((id) => assignedIds.add(id)));
      return devices.filter((d) => !assignedIds.has(d.device_id || (d as any).id));
    }
    const coll = collections.find((c) => c.id === selectedNode.id);
    const ids = coll?.deviceIds || [];
    if (ids.length === 0) return [];
    const idSet = new Set(ids);
    return devices.filter((d) => idSet.has(d.device_id || (d as any).id));
  }, [selectedNode, collections, devices]);

  const filteredCollectionDevices = useMemo(() => {
    if (!collectionSearch.trim()) return displayedDevices;
    const q = collectionSearch.toLowerCase();
    return displayedDevices.filter(
      (d) =>
        (d.name || '').toLowerCase().includes(q) ||
        (d.device_id || (d as any).id || '').toLowerCase().includes(q) ||
        (d.os || '').toLowerCase().includes(q) ||
        (d.platform || '').toLowerCase().includes(q) ||
        ((d as any).ip_address || (d as any).ip || '').toLowerCase().includes(q)
    );
  }, [displayedDevices, collectionSearch]);

  useEffect(() => {
    setCheckedDevices({});
  }, [selectedNode?.id]);

  const isDefaultFolder = (folderId: string) => folderId === 'folder-default';

  const handleCreateFolder = () => setPromptState({ kind: 'create-folder' });

  const handleCreateCollection = (folderId: string) => setPromptState({ kind: 'create-collection', folderId });

  const handleRename = (id: string, type: 'folder' | 'collection') => {
    const item = type === 'folder' ? folders.find((f) => f.id === id) : collections.find((c) => c.id === id);
    setPromptState({ kind: 'rename', id, type, currentName: item?.name || '' });
  };

  const handleMoveCollection = (collectionId: string) => {
    const coll = collections.find((c) => c.id === collectionId);
    if (!coll) return;
    const others = folders.filter((f) => f.id !== coll.folderId);
    if (others.length === 0) {
      toast.error('No other folders to move to. Create a folder first.');
      return;
    }
    setPromptState({
      kind: 'move-collection',
      collectionId,
      collectionName: coll.name,
      options: others,
    });
  };

  const handleDelete = (id: string, type: 'folder' | 'collection') => {
    if (type === 'folder' && isDefaultFolder(id)) return;
    const item = type === 'folder' ? folders.find((f) => f.id === id) : collections.find((c) => c.id === id);
    setConfirmState({
      kind: type === 'folder' ? 'delete-folder' : 'delete-collection',
      id,
      name: item?.name || type,
    });
  };

  const closeConfirm = () => {
    if (confirmLoading) return;
    setConfirmState(null);
  };

  const denyConfirm = () => {
    if (confirmLoading) return;
    setConfirmState(null);
    toast.info('Action denied', 'Not changed');
  };

  const commitConfirm = async () => {
    if (!confirmState) return;
    setConfirmLoading(true);
    try {
      if (confirmState.kind === 'delete-folder') {
        saveFolders(folders.filter((x) => x.id !== confirmState.id));
        saveCollections(collections.filter((c) => c.folderId !== confirmState.id));
        if (selectedNode?.id === confirmState.id) setSelectedNode(null);
        toast.success(`Folder "${confirmState.name}" deleted`, 'Deleted');
      } else if (confirmState.kind === 'delete-collection') {
        saveCollections(collections.filter((c) => c.id !== confirmState.id));
        if (selectedNode?.id === confirmState.id) setSelectedNode(null);
        toast.success(`Collection "${confirmState.name}" deleted`, 'Deleted');
      } else if (confirmState.kind === 'delete-devices') {
        const { ids, names } = confirmState;
        for (const id of ids) {
          try {
            await apiService.deleteGoAgent(id);
          } catch {
            try {
              await fetch(`/api/v1/devices/${id}`, { method: 'DELETE' });
            } catch { /* best effort */ }
          }
        }
        setDevices((prev) => prev.filter((d) => !ids.includes(d.device_id || (d as any).id)));
        const updated = collections.map((c) => ({
          ...c,
          deviceIds: (c.deviceIds || []).filter((id) => !ids.includes(id)),
        }));
        saveCollections(updated);
        if (selectedNode && selectedNode.type === 'collection' && selectedNode.id !== UNASSIGNED_COLLECTION_ID) {
          const coll = updated.find((c) => c.id === selectedNode.id);
          setSelectedNode({ ...selectedNode, deviceIds: coll?.deviceIds || [] } as TreeNode);
        }
        setCheckedDevices({});
        toast.success(
          ids.length === 1 ? `Device "${names[0]}" deleted` : `${ids.length} devices deleted`,
          'Deleted'
        );
      }
      setConfirmState(null);
    } catch (e: any) {
      toast.error(e?.message || 'Action failed');
    } finally {
      setConfirmLoading(false);
    }
  };

  const commitPrompt = async (value: string) => {
    if (!promptState) return;
    if (promptState.kind === 'create-folder') {
      const id = `folder-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      saveFolders([...folders, { id, name: value }]);
      setExpandedIds((prev) => new Set(Array.from(prev).concat(id)));
      toast.success(`Folder "${value}" created`, 'Created');
    } else if (promptState.kind === 'create-collection') {
      const id = `coll-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      saveCollections([
        ...collections,
        { id, name: value, folderId: promptState.folderId, deviceIds: [], createdAt: new Date().toISOString() },
      ]);
      setSelectedNode({ id, name: value, type: 'collection', folderId: promptState.folderId, deviceIds: [] });
      toast.success(`Collection "${value}" created`, 'Created');
    } else if (promptState.kind === 'rename') {
      const { id, type } = promptState;
      if (type === 'folder') {
        if (isDefaultFolder(id)) return;
        saveFolders(folders.map((x) => (x.id === id ? { ...x, name: value } : x)));
      } else {
        saveCollections(collections.map((x) => (x.id === id ? { ...x, name: value } : x)));
      }
      if (selectedNode?.id === id) setSelectedNode({ ...selectedNode, name: value });
      toast.success(`Renamed to "${value}"`, 'Updated');
    } else if (promptState.kind === 'move-collection') {
      const idx = parseInt(value.trim(), 10);
      const target = promptState.options[idx - 1];
      if (!target) {
        toast.error('Enter a valid folder number from the list.');
        return;
      }
      saveCollections(
        collections.map((c) => (c.id === promptState.collectionId ? { ...c, folderId: target.id } : c))
      );
      if (selectedNode?.id === promptState.collectionId) {
        setSelectedNode({ ...selectedNode, folderId: target.id } as TreeNode);
      }
      toast.success(`Moved to "${target.name}"`, 'Updated');
    }
    setPromptState(null);
  };

  const handleAddDevicesToCollection = () => {
    if (!selectedNode || selectedNode.type !== 'collection') return;
    setAddDevicesOpen(true);
  };

  const confirmAddDevices = (deviceIdsToAdd: string[]) => {
    if (!selectedNode || selectedNode.type !== 'collection') return;
    const coll = collections.find((c) => c.id === selectedNode.id);
    if (!coll) return;
    const currentIds = new Set(coll.deviceIds || []);
    const nextIds = Array.from(currentIds).concat(deviceIdsToAdd);
    saveCollections(collections.map((c) => (c.id === selectedNode.id ? { ...c, deviceIds: nextIds } : c)));
    setSelectedNode({ ...selectedNode, deviceIds: nextIds } as TreeNode);
    setAddDevicesOpen(false);
    toast.success(
      deviceIdsToAdd.length === 1 ? '1 device added' : `${deviceIdsToAdd.length} devices added`,
      'Updated'
    );
  };

  const handleRemoveDevice = (deviceId: string) => {
    if (!selectedNode || selectedNode.type !== 'collection') return;
    const coll = collections.find((c) => c.id === selectedNode.id);
    if (!coll) return;
    const nextIds = (coll.deviceIds || []).filter((id) => id !== deviceId);
    saveCollections(collections.map((c) => (c.id === selectedNode.id ? { ...c, deviceIds: nextIds } : c)));
    setSelectedNode({ ...selectedNode, deviceIds: nextIds } as TreeNode);
    toast.success('Device removed from collection', 'Updated');
  };

  const handleMoveDevices = (ids: string[]) => {
    setDeviceIdsToMove(ids);
    setMoveDevicesOpen(true);
  };

  const handleConfirmMoveDevices = (targetCollectionId: string) => {
    const isUnassigned = selectedNode?.id === UNASSIGNED_COLLECTION_ID;
    const updated = collections.map((c) => {
      if (c.id === targetCollectionId) {
        const existing = new Set(c.deviceIds || []);
        deviceIdsToMove.forEach((id) => existing.add(id));
        return { ...c, deviceIds: Array.from(existing) };
      }
      if (!isUnassigned && c.id === selectedNode?.id) {
        return { ...c, deviceIds: (c.deviceIds || []).filter((id) => !deviceIdsToMove.includes(id)) };
      }
      return c;
    });
    saveCollections(updated);
    if (!isUnassigned && selectedNode) {
      const coll = updated.find((c) => c.id === selectedNode.id);
      setSelectedNode({ ...selectedNode, deviceIds: coll?.deviceIds || [] } as TreeNode);
    }
    setCheckedDevices({});
    setMoveDevicesOpen(false);
    const movedCount = deviceIdsToMove.length;
    setDeviceIdsToMove([]);
    toast.success(movedCount === 1 ? '1 device moved' : `${movedCount} devices moved`, 'Updated');
  };

  const handleDeleteDevices = (ids: string[]) => {
    const names = ids.map((id) => {
      const d = devices.find((dev) => (dev.device_id || (dev as any).id) === id);
      return d?.name || id.slice(0, 12);
    });
    setConfirmState({ kind: 'delete-devices', ids, names });
  };

  const confirmTitle =
    confirmState?.kind === 'delete-devices'
      ? confirmState.ids.length === 1
        ? 'Delete device?'
        : 'Delete devices?'
      : confirmState?.kind === 'delete-folder'
        ? 'Delete folder?'
        : confirmState?.kind === 'delete-collection'
          ? 'Delete collection?'
          : 'Confirm';

  const confirmMessage =
    confirmState?.kind === 'delete-devices'
      ? confirmState.ids.length === 1
        ? `Delete device "${confirmState.names[0]}"? This permanently removes it from the system.`
        : `Delete ${confirmState.ids.length} devices? This permanently removes them from the system.`
      : confirmState?.kind === 'delete-folder'
        ? `Delete folder "${confirmState.name}"? Collections inside it will also be removed from the directory.`
        : confirmState?.kind === 'delete-collection'
          ? `Delete collection "${confirmState.name}"?`
          : '';

  const promptTitle =
    promptState?.kind === 'create-folder'
      ? 'Create folder'
      : promptState?.kind === 'create-collection'
        ? 'Create collection'
        : promptState?.kind === 'rename'
          ? 'Rename'
          : promptState?.kind === 'move-collection'
            ? 'Move collection'
            : '';

  const promptMessage =
    promptState?.kind === 'move-collection'
      ? `Move "${promptState.collectionName}" to a folder. Enter the number:\n${promptState.options
          .map((f, i) => `${i + 1}. ${f.name}`)
          .join('\n')}`
      : undefined;

  const promptInitial =
    promptState?.kind === 'rename' ? promptState.currentName : '';

  const directoryDialogs = (
    <>
      <ConfirmDialog
        open={!!confirmState}
        title={confirmTitle}
        message={confirmMessage}
        commitLabel="Commit"
        denyLabel="Deny"
        cancelLabel="Cancel"
        danger
        loading={confirmLoading}
        onCommit={commitConfirm}
        onDeny={denyConfirm}
        onCancel={closeConfirm}
      />
      <PromptDialog
        open={!!promptState}
        title={promptTitle}
        message={promptMessage}
        label={promptState?.kind === 'move-collection' ? 'Folder number' : 'Name'}
        initialValue={promptInitial}
        placeholder={promptState?.kind === 'move-collection' ? 'e.g. 1' : 'Enter a name'}
        commitLabel="Commit"
        denyLabel="Deny"
        cancelLabel="Cancel"
        onCommit={commitPrompt}
        onDeny={() => {
          setPromptState(null);
          toast.info('Action denied', 'Not changed');
        }}
        onCancel={() => setPromptState(null)}
      />
    </>
  );

  if (selectedNode && selectedNode.type === 'collection') {
    const isUnassigned = selectedNode.id === UNASSIGNED_COLLECTION_ID;
    return (
      <div className="p-6 bg-slate-50/50 min-h-[400px]">
        <CollectionDeviceView
          collectionName={selectedNode.name}
          displayedDevices={filteredCollectionDevices}
          loading={loading}
          isUnassigned={isUnassigned}
          onAddDevices={handleAddDevicesToCollection}
          onRemoveDevice={handleRemoveDevice}
          onMoveCollection={!isUnassigned ? () => handleMoveCollection(selectedNode.id) : undefined}
          onDeleteCollection={!isUnassigned ? () => handleDelete(selectedNode.id, 'collection') : undefined}
          onMoveDevices={handleMoveDevices}
          onDeleteDevices={handleDeleteDevices}
          collectionSearch={collectionSearch}
          onSearchChange={setCollectionSearch}
          checkedDevices={checkedDevices}
          onCheckedChange={setCheckedDevices}
          getDeviceMeta={getDeviceMeta}
        />
        {moveDevicesOpen && (
          <MoveToCollectionModal
            deviceNames={deviceIdsToMove.map((id) => {
              const d = devices.find((dev) => (dev.device_id || (dev as any).id) === id);
              return d?.name || id.slice(0, 12);
            })}
            collections={collections}
            folders={folders}
            currentCollectionId={isUnassigned ? undefined : selectedNode.id}
            onClose={() => { setMoveDevicesOpen(false); setDeviceIdsToMove([]); }}
            onConfirm={handleConfirmMoveDevices}
          />
        )}
        {addDevicesOpen && (
          <AddDevicesModal
            devices={devices}
            existingIds={new Set(collections.find((c) => c.id === selectedNode.id)?.deviceIds || [])}
            onClose={() => setAddDevicesOpen(false)}
            onConfirm={confirmAddDevices}
          />
        )}
        {directoryDialogs}
      </div>
    );
  }

  if (selectedNode && selectedNode.type === 'folder') {
    return (
      <div className="p-6 bg-slate-50/50 min-h-[400px]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">{selectedNode.name}</h2>
            <p className="text-sm text-slate-500 mt-0.5">Folder management — create, delete, or move collections</p>
          </div>
          <button
            type="button"
            onClick={() => handleCreateCollection(selectedNode.id)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium shrink-0"
          >
            <Plus size={16} /> Create collection
          </button>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          {(collectionsByFolder.get(selectedNode.id) || []).length === 0 ? (
            <p className="text-slate-500 text-sm py-4">No collections in this folder. Create one to add devices.</p>
          ) : (
            <div className="space-y-2">
              {(collectionsByFolder.get(selectedNode.id) || []).map((c) => (
                <div key={c.id} className="flex items-center justify-between py-3 px-4 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="font-medium text-slate-800">{c.name}</span>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => handleMoveCollection(c.id)} className="text-sm text-slate-600 hover:text-slate-800 hover:underline">
                      Move
                    </button>
                    <button type="button" onClick={() => handleDelete(c.id, 'collection')} className="text-sm text-red-600 hover:text-red-700 hover:underline">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {directoryDialogs}
      </div>
    );
  }

  return (
    <div className="p-6 bg-slate-50/50 min-h-[400px]">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Directory</h2>
          <p className="text-sm text-slate-500 mt-0.5">Create or delete folders to organize collections</p>
        </div>
        <button
          type="button"
          onClick={handleCreateFolder}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium shrink-0"
        >
          <Plus size={16} /> Create folder
        </button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        {folders.filter((f) => f.id !== 'folder-default').length === 0 ? (
          <p className="text-slate-500 text-sm py-4">No folders yet. Create a folder to add collections.</p>
        ) : (
          <div className="space-y-2">
            {folders
              .filter((f) => f.id !== 'folder-default')
              .map((f) => (
                <div key={f.id} className="flex items-center justify-between py-3 px-4 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="font-medium text-slate-800">{f.name}</span>
                  {!isDefaultFolder(f.id) && (
                    <button type="button" onClick={() => handleDelete(f.id, 'folder')} className="text-sm text-red-600 hover:text-red-700 hover:underline">
                      Delete folder
                    </button>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
      {directoryDialogs}
    </div>
  );
};

export default DirectoryView;
